import { createSelector } from 'reselect';

import { OnboardingStatus, Permission } from '../../types';
import { StateType } from '../index';
import Immutable from 'immutable';

export const getUserIdSelector = (state: StateType) => state.getIn(['user', 'data', 'id']) as string | undefined;
export const getUserIdFromUserSelector = (state: StateType) => state.getIn(['user', 'userId']) as string;
export const getIsLoggedInSelector = (state: StateType) => state.getIn(['user', 'isLoggedIn']) as boolean;
export const getIsFetchingReverifySelector = (state: StateType) => state.getIn(['user', 'isFetchingReverify']);
export const getInquiryIdSelector = (state: StateType) => state.getIn(['user', 'inquiryId']);
export const getIsSelfieError = (state: StateType) => state.getIn(['user', 'isSelfieError']);
export const getReverifySentSelector = (state: StateType) => state.getIn(['user', 'reverifySent']);
export const getIsPasswordResetSelector = (state: StateType) => state.getIn(['user', 'isPasswordReset']);
export const getRedirectUrl = (state: StateType) => state.getIn(['user', 'redirectUrl']);
export const getUserDataDateCreatedSelector = (state: StateType) => state.getIn(['user', 'data', 'dateCreated']);
export const getUserDataDateApprovedSelector = (state: StateType) => state.getIn(['user', 'data', 'dateApproved']);
export const getUserDataEducationSelector = (state: StateType) => state.getIn(['user', 'data', 'education']);
export const getUserDataEducationUniversitySelector = (state: StateType) => state.getIn(['user', 'data', 'education', 'university']);
export const getUserDataAddressSelector = (state: StateType) => state.getIn(['user', 'data', 'address']);
export const getUserDataAddressCountrySelector = (state: StateType) => state.getIn(['user', 'data', 'address', 'country']);

export const getUserDataCampaignSelector = (state: StateType) => state.getIn(['user', 'data', 'auxiliary', 'campaign', 'campaign']);
export const getUserTimeToLogoutSelector = (state: StateType) => state.getIn(['user', 'timeToLogout']);
export const getUserHasLoggedInBeforeSelector = (state: StateType) => state.getIn(['user', 'hasLoggedInBefore']);
export const getUserEmailSelector = (state: StateType) => state.getIn(['user', 'data', 'email']) as string | undefined;
export const getUserFirstNameSelector = (state: StateType) => state.getIn(['user', 'data', 'firstName']) as string | undefined;
export const getUserLastNameSelector = (state: StateType) => state.getIn(['user', 'data', 'lastName']) as string | undefined;
export const getUserDataSelector = (state: StateType) => state.getIn(['user', 'data']) as Immutable.Map<string, any>;
export const getUserShowMessageSelector = (state: StateType) => state.getIn(['user', 'showMessage']);
export const getUserAgreementsSelector = (state: StateType) => {
  const userAgreements = state.getIn(['user', 'userAgreements']);
  return userAgreements;
};
export const getUserIsFetchingSelector = (state: StateType) => state.getIn(['user', 'isFetching']) as boolean;
export const getUserFetchingChangePasswordSelector = (state: StateType) => state.getIn(['user', 'fetchingChangePassword']);

export const getUserFetchingUserAgreementsSelector = (state: StateType) => state.getIn(['user', 'fetchingUserAgreements']);
export const getUserPermissionsSelector = (state: StateType) => state.getIn(['user', 'permissions']) as Immutable.List<Permission>;
// Memoized so the Set reference is stable across renders unless the underlying permissions
// list actually changes. Returning `new Set(...)` on every call caused React-Redux v8's
// `useSelector` (via useSyncExternalStore, stricter than v7's shallow check) to re-render every
// consumer on every store tick — which on the Operators page (many OperatorTable instances
// each reading permissions) spiraled into a CPU loop / page crash under React 19.
// Assumes `permissions` is populated: like the pre-memoization original, this calls `.toJS()`
// directly, so it is only reached post-auth (its consumers are permission checks).
export const getUserPermissionSet = createSelector(
  getUserPermissionsSelector,
  (permissions): Set<Permission> => new Set(permissions.toJS() as Permission[])
);
export const getUserSettingsSelector = (state: StateType) => state.getIn(['user', 'settings']);
export const getUserFetchingAuthSelector = (state: StateType) => state.getIn(['user', 'fetchingAuth']);
export const getUserIsFetchingLogoutSelector = (state: StateType) => state.getIn(['user', 'isFetchingLogout']);
export const getUserNeedsRecertificationSelector = (state: StateType) => state.getIn(['user', 'needsRecertification']);
export const getUserForgotPasswordEmailSentSelector = (state: StateType) => state.getIn(['user', 'forgotPasswordEmailSent']);
export const getUserOnboardingSelector = (state: StateType) => state.getIn(['user', 'data', 'onboarding']);
export const getUserOnboardingStatusSelector =
  // @ts-expect-error ImmutableJS getIn returns unknown
  (state: StateType): OnboardingStatus => state.getIn(['user', 'data', 'onboarding', 'status']);

export const hasVisualizationPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('VISUALIZATION')
);

export const hasConsultantPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('CONSULTANT')
);

export const hasTutorialPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions?.includes('TUTORIAL') as boolean | undefined
);

export const hasEmployeePermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('WORLDQUANT')
);

export const hasResearcherPermission = createSelector(getUserPermissionSet, permissions => permissions && permissions.has('RESEARCHER'));

export const hasBrainLabsPermission = createSelector(getUserPermissionSet, permissions => permissions?.has('BRAIN_LABS') ?? false);

export const hasReferralPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('REFERRAL')
);

export const hasMultiSimulationPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('MULTI_SIMULATION')
);

export const hasProdPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('PROD_ALPHAS')
);

export const hasTokenPermission = createSelector(getUserPermissionsSelector, permissions => permissions && permissions.includes('TOKEN'));

export const hasWorkdayPermission = createSelector(
  getUserPermissionsSelector,
  permissions => permissions && permissions.includes('WORKDAY')
);

export const hasConsultantOrResearcherPermission = createSelector(
  hasResearcherPermission,
  hasConsultantPermission,
  (researcher, consultant) => researcher || consultant
);
